import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { playTone, stopAll } from "@/lib/audioEngine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

export default function FrequencyGiftReceiver() {
  const { giftId } = useParams();
  const navigate = useNavigate();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadGift();
  }, [giftId]);

  const loadGift = async () => {
    try {
      const res = await axios.get(`${API}/frequency-gifts/${giftId}`);
      setGift(res.data);
    } catch (e) {
      console.error(e);
      if (e.response?.status === 404) {
        setError("Gift not found");
      } else if (e.response?.status === 410) {
        setError("This gift has expired");
      } else {
        setError("Failed to load gift");
      }
    } finally {
      setLoading(false);
    }
  };

  const acceptAndPlay = async () => {
    try {
      // Mark as accepted
      await axios.post(`${API}/frequency-gifts/${giftId}/accept`);
      
      // Play frequency
      const calib = Number(localStorage.getItem("calibrationCents") || 0);
      stopAll();
      playTone({ freq: gift.frequency_hz, gain: 0.3, offsetCents: calib });
      setPlaying(true);
      
      toast.success(`Playing ${gift.frequency_hz} Hz! 🎵`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to accept gift");
    }
  };

  const stopPlaying = () => {
    stopAll();
    setPlaying(false);
    toast.info("Stopped");
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading gift...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 space-y-6">
        <Card className="sg-frame">
          <CardHeader>
            <CardTitle className="text-red-500">⚠️ {error}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              This frequency gift is no longer available.
            </p>
            <Button onClick={() => navigate("/")} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#a4ff2d]">
          🎁 Frequency Gift
        </h1>
        <p className="text-sm text-muted-foreground">
          Someone wants to lift your spirits!
        </p>
      </div>

      <Card className="sg-frame">
        <CardContent className="pt-6 space-y-6">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r from-[#ff1744] to-[#a4ff2d] p-1">
              <div className="w-full h-full rounded-full bg-black flex items-center justify-center">
                <span className="text-4xl">🎵</span>
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">From</p>
              <p className="text-2xl font-semibold text-[#a4ff2d]">{gift.from_user}</p>
              {gift.to_user && (
                <>
                  <p className="text-sm text-muted-foreground mt-2">To</p>
                  <p className="text-lg font-medium">{gift.to_user}</p>
                </>
              )}
            </div>

            <div className="p-4 bg-black/40 rounded border border-[#a4ff2d]/20">
              <p className="text-lg font-semibold text-[#a4ff2d] mb-2">
                {gift.frequency_hz} Hz
              </p>
              {gift.message && (
                <p className="text-sm text-muted-foreground italic">
                  "{gift.message}"
                </p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {!playing ? (
              <Button onClick={acceptAndPlay} className="w-full h-12 text-lg">
                ▶️ Accept & Play Gift
              </Button>
            ) : (
              <Button onClick={stopPlaying} variant="destructive" className="w-full h-12 text-lg">
                ⏹️ Stop
              </Button>
            )}

            <Button onClick={() => navigate("/")} variant="outline" className="w-full">
              Go to Dashboard
            </Button>
          </div>

          {gift.accepted && (
            <p className="text-xs text-center text-muted-foreground">
              ✓ Gift already accepted
            </p>
          )}

          <div className="text-xs text-center text-muted-foreground">
            <p>Gift created: {new Date(gift.created_at).toLocaleString()}</p>
            {gift.expires_at && (
              <p>Expires: {new Date(gift.expires_at).toLocaleString()}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="sg-frame">
        <CardHeader>
          <CardTitle className="text-sm">About {gift.frequency_hz} Hz</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            This frequency has been carefully selected to help lift your spirits and enhance your well-being. 
            Listen with headphones for the best experience. Allow yourself to relax and let the sound waves 
            work their magic.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
